import store from '@/store'
import {each} from 'lodash'
import {EventBus, events} from "@/utils/event-bus";

export const errorCodes = {
    notVerified: 20202,
    unauthorized: 10401,
    missingDirectDebit: 20303
};

class Api {

    baseUrl = process.env.VUE_APP_API_BASE_URL;
    _basePath = '/v1';
    axios = require('axios');
    _token = null;

    constructor(token = null) {
        this._token = token || store.getters['auth/getAccessToken'];
        this.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
        this.axios.defaults.baseURL = this.url

        this.axios.interceptors.response.use(response => response, error => {
            let response = error.response;
            let status = response.status;
            let data = response.data;

            if (response && status === 400 && data.error && data.error.code === errorCodes.notVerified) {
                EventBus.$emit(events.auth.notVerified)
            }

            if (response && status === 400 && data.error && data.error.code === errorCodes.missingDirectDebit) {
                EventBus.$emit(events.auth.missingDirectDebit)
            }


            if (response && status === 401 && data.error && data.error.code === errorCodes.unauthorized && response.config.url !== '/auth/signin') {
                EventBus.$emit(events.auth.signout)
            }
            return Promise.reject(error);
        });
    }

    get url() {
        return this.baseUrl + this._basePath;
    }

    getQueryFilter(queryFields) {
        let query = '';

        each(queryFields, function (options, resource) {
            query += resource + ':';
            each(options, function (fields, option) {
                query += option + '{' + fields.join(',') + '};';
            });
        });

        return query;
    }

    addAuthorization() {
        this.axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }

    get token() {
        return this._token;
    }

    set token(token) {
        this._token = token;
        this.addAuthorization();
    }

    address(postcode) {
        return this.axios.get(`/address`, {
            params: {
                postcode: postcode
            }
        });
    }

    survey() {
        return this.axios.get(`/survey`);
    }

    storeSurvey(survey) {
        if (this._token) {
            this.addAuthorization()
        }
        return this.axios.post(`/survey`, survey)
    }

    suppliers() {
        return this.axios.get(`/suppliers`);
    }

    tariffs(supplier, region, energySource, e7, paymentScheme = null) {
        let params = {
            region: region,
            service: energySource,
            e7: e7,
        };
        if (paymentScheme) {
            params['payment'] = paymentScheme
        }
        return this.axios.post(`/suppliers/${supplier}/products`, params)
    }

    tariff(id, params) {
        this.addAuthorization()

        let queryParams = {}

        if (params) {
            queryParams = {
                q: this.getQueryFilter(params)
            }
        }
        return this.axios.get(`tariffs/${id}`, {
            params: queryParams
        })
    }

    signup(signupObject) {
        return this.axios.post(`/auth/signup`, signupObject);
    }

    signin(signinObject) {
        return this.axios.post(`/auth/signin`, signinObject);
    }

    register(data) {
        return this.axios.post(`/register`, data);
    }

    socialauth(socialObject) {
        return this.axios.post(`/auth/social`, socialObject);
    }

    analytics(event) {
        return this.axios.post(`/a/` + event);
    }

    profile() {
        this.addAuthorization();
        return this.axios.get(`/profile`);
    }

    balance() {
        this.addAuthorization();
        const queryParams = {
            q: this.getQueryFilter({
                users: {
                    fields: ['balance']
                }
            })
        }
        return this.axios.get(`/profile`, {
            params: queryParams
        });
    }

    savings() {
        this.addAuthorization();
        const queryParams = {
            q: this.getQueryFilter({

                users: {
                    fields: ['savings']
                }
            })
        }
        return this.axios.get(`/profile`, {
            params: queryParams
        });
    }

    updateProfile(firstName, lastName, title) {
        this.addAuthorization();
        return this.axios.put(`/profile`, {
            title: title,
            first_name: firstName,
            last_name: lastName
        });
    }

    updateEmail(email) {
        this.addAuthorization();
        return this.axios.put(`/email`, {
            email: email
        });
    }

    updatePhone(phone) {
        this.addAuthorization();
        return this.axios.put(`/profile/phone`, {
            phone: phone
        });
    }

    updateAddress(address) {
        this.addAuthorization();
        return this.axios.put(`/profile/address`, address);
    }

    verify(token) {
        this.addAuthorization();
        return this.axios.post(`/auth/verify`, {
            code: token
        });
    }

    resendVerification() {
        this.addAuthorization();
        return this.axios.post(`/auth/resend`);
    }

    getRecommendation(code, params) {
        this.addAuthorization()
        let queryParams = {}

        if (params) {
            queryParams = {
                q: this.getQueryFilter(params)
            }
        }
        return this.axios.get(`/recommendations/${code}`, {
            params: queryParams
        })
    }

    confirmSwitch(code) {
        this.addAuthorization()
        return this.axios.get(`/recommendations/${code}/switch`)
    }

    rewards(page) {

        page = page || 1

        this.addAuthorization()

        const queryParams = {
            q: this.getQueryFilter({
                rewards: {
                    fields: ['id', 'amount', 'created_at', 'referee']
                },
                users: {
                    fields: ['id', 'first_name', 'last_name']
                }
            }),
            page: page
        }

        return this.axios.get(`/rewards`, {
            params: queryParams
        })
    }

    payouts(page) {
        page = page || 1

        this.addAuthorization()

        const queryParams = {
            q: this.getQueryFilter({payouts: {fields: ['id', 'amount', 'created_at']}}),
            page: page
        }

        return this.axios.get(`/payouts`, {
            params: queryParams
        })
    }

    properties() {
        this.addAuthorization()
        const queryParams = {
            q: this.getQueryFilter({properties: {fields: ['id', 'currentPropertyAddress']}})
        }
        return this.axios.get(`properties`, {
            params: queryParams
        })
    }

    property(id) {
        this.addAuthorization()
        const queryParams = {
            q: this.getQueryFilter({
                properties: {
                    fields: [
                        'id',
                        'currentPropertyPostcode',
                        'currentPropertyAddress',
                        'currentPropertyType',
                        'currentPropertySize',
                        'currentPropertyOwnership',

                        'currentPropertyAddressPayload',
                        'currentPropertyRegion',

                        'mpan',
                        'mprn',
                        'usage',
                        'recommendation'
                    ]
                }
            })
        }
        return this.axios.get(`properties/${id}`, {
            params: queryParams
        })
    }

    propertyTariff(id) {
        this.addAuthorization()
        const queryParams = {
            // q: this.getQueryFilter({properties: {fields: ['currentPropertyAddress']}})
        }
        return this.axios.get(`properties/${id}/tariff`, {
            params: queryParams
        })
    }

    updateProperty(id, data) {
        this.addAuthorization()
        const queryParams = {
            // q: this.getQueryFilter({properties: {fields: ['currentPropertyAddress']}})
        }
        return this.axios.put(`properties/${id}`, data, {
            params: queryParams
        })
    }

    updatePropertySupply(id, choice) {
        this.addAuthorization()

        return this.axios.post(`properties/${id}/survey`, choice)
    }

    notifications() {
        this.addAuthorization()
        const queryParams = {
            q: this.getQueryFilter({
                recommendations: {
                    fields: [
                        'id',
                        'code',
                        'savings',
                        'estimated',
                        'product',
                        'tariff',
                        'editable',
                        'worthless',
                        'property'
                    ]
                },

                properties: {fields: ['currentPropertyAddress']}
            })
        }
        return this.axios.get(`notifications`, {
            params: queryParams
        })
    }

    passwordReset(email) {
        return this.axios.post('auth/forgot', {
            email: email
        })
    }

    passwordSet(password, code) {
        return this.axios.post('auth/reset', {
            password: password,
            code: code,
        })
    }

    sendSupport(topic, message) {
        return this.axios.post('support', {
            topic,
            message
        })
    }

    savePaymentDetails(details) {
        this.addAuthorization()
        return this.axios.put('payment', details)
    }

    withdraw(data) {
        this.addAuthorization()

        return this.axios.post('payouts/withdraw', data)
    }
}

export default new Api();
